// URL routing utilities for hash-based navigation with query parameters

export interface RouteParams {
  page: string
  id?: string
  view?: string  // Modal view ID (e.g., product ID to show in detail modal)
  org?: string   // Selected organisation ID
  search?: string
  status?: string
  category?: string
  role?: string
  priority?: string
  type?: string
  assignee?: string
  asset?: string
  showDeleted?: string  // "true" to show deleted items
}

// Parse the current URL hash into route params
export function parseHash(): RouteParams {
  const hash = window.location.hash.replace('#/', '').replace('#', '')
  const [pathPart, queryPart] = hash.split('?')

  // Parse query parameters
  const params: RouteParams = { page: 'dashboard' }
  if (queryPart) {
    const searchParams = new URLSearchParams(queryPart)
    if (searchParams.get('view')) params.view = searchParams.get('view')!
    if (searchParams.get('org')) params.org = searchParams.get('org')!
    if (searchParams.get('search')) params.search = searchParams.get('search')!
    if (searchParams.get('status')) params.status = searchParams.get('status')!
    if (searchParams.get('category')) params.category = searchParams.get('category')!
    if (searchParams.get('role')) params.role = searchParams.get('role')!
    if (searchParams.get('priority')) params.priority = searchParams.get('priority')!
    if (searchParams.get('type')) params.type = searchParams.get('type')!
    if (searchParams.get('assignee')) params.assignee = searchParams.get('assignee')!
    if (searchParams.get('asset')) params.asset = searchParams.get('asset')!
    if (searchParams.get('showDeleted')) params.showDeleted = searchParams.get('showDeleted')!
  }

  // Parse path
  const pathSegments = pathPart.split('/')

  // Handle special routes
  if (pathPart.startsWith('public/passport/')) {
    params.page = 'public-passport'
    params.id = pathSegments[2]
    return params
  }

  if (pathPart.startsWith('dpp-template-edit/')) {
    params.page = 'dpp-template-edit'
    params.id = pathSegments[1]
    return params
  }

  if (pathPart.startsWith('product-edit/')) {
    params.page = 'product-edit'
    params.id = pathSegments[1]
    return params
  }

  if (pathPart.startsWith('workorder-edit/')) {
    params.page = 'workorder-edit'
    params.id = pathSegments[1]
    return params
  }

  if (pathPart.match(/^workorder\/[^/]+$/)) {
    params.page = 'workorders'
    params.view = pathSegments[1]
    return params
  }

  if (pathPart.startsWith('workorder-create/asset/')) {
    params.page = 'workorder-create'
    params.asset = pathSegments[2]
    return params
  }

  // Standard page
  params.page = pathPart || 'dashboard'

  return params
}

// Build a URL hash from route params
export function buildHash(params: RouteParams): string {
  const { page, id, ...queryParams } = params

  // Build path part
  let path = page
  if (id) {
    path = `${page}/${id}`
  }

  // Build query string from non-empty params
  const queryParts: string[] = []
  for (const [key, value] of Object.entries(queryParams)) {
    if (value) {
      queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    }
  }

  const query = queryParts.length > 0 ? `?${queryParts.join('&')}` : ''
  return `#/${path}${query}`
}

// Navigate to a new route (updates URL and triggers hashchange)
export function navigate(params: RouteParams, replace = false): void {
  const hash = buildHash(params)
  if (replace) {
    // Replace current history entry
    const url = new URL(window.location.href)
    url.hash = hash.substring(1) // Remove leading #
    window.history.replaceState(null, '', url.toString())
    // Manually dispatch hashchange since replaceState doesn't trigger it
    window.dispatchEvent(new HashChangeEvent('hashchange'))
  } else {
    // Add new history entry
    window.location.hash = hash
  }
}

// Update current route with new params (merges with existing)
export function updateRoute(updates: Partial<RouteParams>, replace = false): void {
  const current = parseHash()
  navigate({ ...current, ...updates }, replace)
}

// Clear specific params from current route
export function clearRouteParams(keys: (keyof RouteParams)[], replace = true): void {
  const current = parseHash()
  for (const key of keys) {
    delete current[key]
  }
  navigate(current, replace)
}
